import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink } from "lucide-react";
import Reveal from "@/components/Reveal";
import { BOOKS, IMAGES } from "@/lib/site";

export default function Books() {
  const main = BOOKS[0];
  const firstYear = BOOKS.find((b) => b.id === "first-year-after-goodbye");
  const workbooks = BOOKS.filter((b) =>
    ["hospice-workbook", "bereavement-workbook"].includes(b.id),
  );
  const collection = [
    {
      key: "main",
      title: "From Diagnosis to Goodbye\u2122",
      line: "The foundational book, restoring the patient\u2019s voice throughout the journey.",
      price: "$16.99",
      cover: main.cover,
      amazon: main.amazon,
    },
    {
      key: "hospice",
      title: "Hospice Workbook",
      line: "A guided companion for preparation, presence, and meaningful family conversations.",
      price: "$14.99",
      cover: workbooks[0].cover,
      amazon: workbooks[0].amazon,
    },
    {
      key: "bereavement",
      title: "Bereavement Workbook",
      line: "A guided companion for reflection, healing, and life after loss.",
      price: "$14.99",
      cover: workbooks[1].cover,
      amazon: workbooks[1].amazon,
    },
  ];
  return (
    <div className="bg-ivory">
      <section className="mx-auto max-w-6xl px-5 sm:px-8 pt-36 pb-16 sm:pt-44">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
            Books & workbooks
          </p>
          <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl text-navy tracking-tight max-w-3xl leading-[1.08]">
            Words for the hardest conversations.
          </h1>
          <p className="mt-7 max-w-2xl text-lg leading-relaxed text-stone2">
            Two books — one for the journey, one for after — and the workbooks
            that walk alongside them.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-6xl px-5 sm:px-8 pb-24">
        <Reveal>
          <div className="bg-sand border border-line rounded-md">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 p-8 sm:p-12 lg:p-16 items-center">
              <div className="lg:col-span-5">
                <div className="relative max-w-[300px] mx-auto lg:mx-0">
                  <div
                    className="absolute -inset-8 -z-10"
                    style={{
                      background:
                        "radial-gradient(circle at 50% 45%, rgba(200,138,46,0.22), transparent 68%)",
                    }}
                    aria-hidden="true"
                  />
                  <img
                    src={main.cover}
                    alt={`Cover of ${main.title}: ${main.subtitle}`}
                    data-testid="books-main-cover"
                    className="w-full h-auto rounded-sm shadow-[0_36px_70px_-24px_rgba(11,21,34,0.5)] hover:-translate-y-2 transition-transform duration-500"
                  />
                </div>
              </div>
              <div className="lg:col-span-7">
                <span className="inline-block text-[10px] uppercase tracking-[0.18em] bg-gold-light text-gold-hover px-3 py-1 rounded-full">
                  {main.tag}
                </span>
                <h2 className="mt-5 font-serif text-3xl sm:text-4xl text-navy leading-tight">
                  From Diagnosis to Goodbye
                  <sup className="align-super text-[0.45em] font-sans font-normal text-gold">™</sup>
                </h2>
                <p className="mt-3 font-serif italic text-lg text-gold">
                  {main.subtitle}
                </p>
                <div className="mt-7 space-y-5 text-stone2 leading-relaxed">
                  {main.description.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
                <p className="mt-7 text-sm text-mist italic">{main.forWhom}</p>
                <div className="mt-8 flex flex-wrap items-center gap-5">
                  <a
                    href={main.amazon}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="books-buy-amazon-from-diagnosis-to-goodbye"
                    className="inline-flex items-center gap-2 bg-gold text-navy px-6 py-3 rounded-full text-sm font-semibold hover:bg-gold-hover hover:text-ivory transition-colors duration-200"
                  >
                    Buy on Amazon <ExternalLink className="w-4 h-4" />
                  </a>
                  <Link
                    to="/group-copies"
                    data-testid="books-group-copies-link"
                    className="inline-flex items-center gap-2 text-sm font-medium text-burgundy hover:text-gold-hover transition-colors"
                  >
                    Request group copies <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>

                <div className="mt-10 border-t border-line-strong/70 pt-8" data-testid="five-voices-section">
                  <h3 className="font-serif text-xl sm:text-2xl text-navy tracking-tight">
                    The Five Guiding Voices of the Journey
                  </h3>
                  <p className="mt-2 text-sm text-mist italic">
                    Recurring features throughout the book — the full
                    explanations remain in its pages.
                  </p>
                  <div className="mt-7 grid sm:grid-cols-2 gap-x-10 gap-y-6">
                    <div>
                      <p className="font-serif font-semibold text-navy">
                        The Unseen Voice
                      </p>
                      <p className="mt-1.5 text-[15px] leading-relaxed text-stone2">
                        Imagined inner reflections inspired by patients I have
                        accompanied, giving words to feelings that often go
                        unspoken.{" "}
                        <em className="italic text-mist">
                          These are not direct accounts from any one person.
                        </em>
                      </p>
                    </div>
                    <div>
                      <p className="font-serif font-semibold text-navy">
                        The Chaplain&rsquo;s Observation
                      </p>
                      <p className="mt-1.5 text-[15px] leading-relaxed text-stone2">
                        Lessons from years of walking beside patients and
                        families, offering perspective and practical wisdom.
                      </p>
                    </div>
                    <div>
                      <p className="font-serif font-semibold text-navy">
                        Behind the Scenes
                      </p>
                      <p className="mt-1.5 text-[15px] leading-relaxed text-stone2">
                        A closer look at the emotions, fears,
                        misunderstandings, and acts of love beneath what we
                        see on the surface.
                      </p>
                    </div>
                    <div>
                      <p className="font-serif font-semibold text-navy">
                        Derrick&rsquo;s Notebook
                      </p>
                      <p className="mt-1.5 text-[15px] leading-relaxed text-stone2">
                        Personal reflections on what the bedside has taught me
                        about life, love, grief, faith, and presence.
                      </p>
                    </div>
                    <div className="sm:col-span-2">
                      <p className="font-serif font-semibold text-navy">
                        Walking Forward Together
                      </p>
                      <p className="mt-1.5 text-[15px] leading-relaxed text-stone2">
                        Gentle questions and practical next steps that help
                        readers carry each chapter into conversation and
                        action.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <section
        className="mx-auto max-w-6xl px-5 sm:px-8 pb-24"
        data-testid="reader-testimonial-section"
      >
        <Reveal>
          <figure className="max-w-3xl mx-auto text-center">
            <span className="block h-px w-16 bg-gold/60 mx-auto" aria-hidden="true" />
            <blockquote className="mt-8">
              <p className="font-serif italic text-2xl sm:text-3xl leading-snug text-navy text-balance">
                &ldquo;Your book gives families an opportunity to think about
                these things while they still have time to ask. I think that
                is incredibly valuable.&rdquo;
              </p>
            </blockquote>
            <figcaption className="mt-6 font-serif italic text-sm text-mist">
              A mother reflecting after reading the first ten chapters.
            </figcaption>
          </figure>
        </Reveal>
      </section>

      <section className="mx-auto max-w-6xl px-5 sm:px-8 pb-24">
        <Reveal>
          <h2 className="font-serif text-2xl sm:text-3xl text-navy tracking-tight">
            The companion workbooks
          </h2>
          <p className="mt-4 max-w-2xl text-stone2 leading-relaxed">
            Guided company for the season of caring and the season after loss —
            with space to write and room to think.
          </p>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 gap-6">
          {workbooks.map((b, i) => (
            <Reveal key={b.id} delay={0.08 * i} className="h-full">
              <div
                className="h-full flex flex-col bg-white border border-line rounded-md overflow-hidden hover:shadow-[0_24px_48px_-28px_rgba(11,21,34,0.3)] transition-shadow duration-300"
                data-testid={`books-card-${b.id}`}
              >
                <div className="pt-10 px-10">
                  <img
                    src={b.cover}
                    alt={`Cover of ${b.title}: ${b.subtitle}`}
                    className="mx-auto max-w-[190px] w-full h-auto rounded-sm shadow-[0_20px_36px_-14px_rgba(11,21,34,0.4)] hover:-translate-y-2 transition-transform duration-500"
                  />
                </div>
                <div className="p-8 flex-1 flex flex-col">
                  <span className="inline-block self-start text-[10px] uppercase tracking-[0.18em] text-gold">
                    {b.tag}
                  </span>
                  <h3 className="mt-3 font-serif text-xl sm:text-2xl text-navy leading-snug">
                    {b.title}
                  </h3>
                  <p className="mt-2 font-serif italic text-sm text-gold">
                    {b.subtitle}
                  </p>
                  <p className="mt-4 text-sm leading-relaxed text-stone2 flex-1">
                    {b.description[0]}
                  </p>
                  {b.amazon && (
                    <a
                      href={b.amazon}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`books-buy-amazon-${b.id}`}
                      className="mt-5 self-start inline-flex items-center gap-1.5 text-sm font-medium text-navy border border-line-strong px-4 py-2 rounded-full hover:border-gold hover:text-gold-hover transition-colors duration-200"
                    >
                      Buy on Amazon <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section
        className="mx-auto max-w-6xl px-5 sm:px-8 pb-24"
        data-testid="books-companion-collection"
      >
        <Reveal>
          <div className="bg-sand border border-line rounded-md p-8 sm:p-12">
            <h2 className="font-serif text-2xl sm:text-3xl text-navy tracking-tight">
              The From Diagnosis to Goodbye&trade; Companion Collection
            </h2>
            <p className="mt-3 font-serif italic text-lg text-gold-hover">
              Three resources. One compassionate journey.
            </p>
            <p className="mt-5 max-w-3xl text-stone2 leading-relaxed">
              The From Diagnosis to Goodbye&trade; Companion Collection brings
              together three resources designed to support patients,
              caregivers, and families through serious illness, hospice care,
              and bereavement.
            </p>
            <div className="mt-10 grid gap-6 sm:grid-cols-3">
              {collection.map((c, i) => (
                <Reveal key={c.key} delay={0.06 * i} className="h-full">
                  <div
                    className="flex flex-col h-full bg-white border border-line rounded-md overflow-hidden"
                    data-testid={`books-collection-card-${c.key}`}
                  >
                    <div className="pt-8 px-8">
                      <img
                        src={c.cover}
                        alt={`Cover of ${c.title}`}
                        className="mx-auto max-w-[150px] w-full h-auto rounded-sm shadow-[0_18px_32px_-14px_rgba(11,21,34,0.4)]"
                      />
                    </div>
                    <div className="p-6 flex-1 flex flex-col">
                      <h3 className="font-serif text-lg text-navy leading-snug">
                        {c.title}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-stone2 flex-1">
                        {c.line}
                      </p>
                      <p
                        className="mt-4 font-serif text-xl text-navy"
                        data-testid={`books-collection-price-${c.key}`}
                      >
                        {c.price}
                      </p>
                      <a
                        href={c.amazon}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`books-collection-buy-${c.key}`}
                        className="mt-3 inline-flex items-center justify-center gap-2 bg-gold text-navy px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-gold-hover hover:text-ivory transition-colors duration-200"
                      >
                        Buy on Amazon <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <p className="mt-6 max-w-3xl text-stone2 leading-relaxed">
              Whether you are beginning the journey, providing care, or
              navigating life after loss, these three resources offer
              guidance, reflection, and compassionate companionship. Purchase
              the resources that meet your needs, or explore all three.
            </p>
          </div>
        </Reveal>
      </section>

      <section
        className="mx-auto max-w-6xl px-5 sm:px-8 pb-24"
        data-testid="books-qa-section"
      >
        <Reveal>
          <div className="max-w-3xl">
            <h2 className="font-serif text-2xl sm:text-3xl text-navy tracking-tight">
              Why Two Separate Workbooks?
            </h2>
            <h3
              className="mt-8 border-l-2 border-gold/70 pl-6 font-serif text-lg sm:text-xl text-navy leading-snug"
              data-testid="books-qa-question"
            >
              Why are the Hospice and Bereavement Workbooks separate?
            </h3>
            <div className="mt-6 space-y-5 text-stone2 leading-relaxed">
              <p>
                Every patient&rsquo;s journey is different, and not everyone
                who enters hospice care dies while receiving hospice services.
              </p>
              <p>
                Some patients stabilize or improve enough that they no longer
                meet hospice eligibility requirements and are discharged from
                care&mdash;sometimes referred to as
                &ldquo;graduating&rdquo; from hospice. Others may continue
                receiving hospice services beyond the initial six-month
                prognosis when they remain eligible.
              </p>
              <p>
                The From Diagnosis to Goodbye&trade; Hospice Workbook
                accompanies Parts One, Two, and Three of the main book,
                focusing on preparation, presence, meaningful family
                conversations, and preserving the patient&rsquo;s voice while
                they are living and receiving care.
              </p>
              <p>
                The From Diagnosis to Goodbye&trade; Bereavement Workbook
                accompanies Part Four, providing reflection, encouragement,
                and practical emotional support to families and loved ones
                following a death.
              </p>
              <p>
                These workbooks are intentionally separate because hospice
                care should never be presented as though death has already
                occurred or must occur within a fixed timeframe.
              </p>
              <p>
                The Hospice Workbook encourages patients and families to
                remain engaged in the present, honoring the person&rsquo;s
                wishes, experiences, and voice. The Bereavement Workbook
                provides a separate space for grief and adjustment when a
                loved one has died.
              </p>
              <p>
                Both resources honor the same principle: Meet patients and
                families where they are in the journey, without assuming what
                comes next.
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-6xl px-5 sm:px-8 pb-24">
        <Reveal>
          <h2
            className="font-serif text-2xl sm:text-3xl text-navy tracking-tight"
            data-testid="books-bereavement-heading"
          >
            A Separate Bereavement Journey
          </h2>
        </Reveal>
        <div className="mt-12 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <Reveal>
              <span className="inline-block text-[10px] uppercase tracking-[0.18em] text-gold">
                {firstYear.tag}
              </span>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl text-navy leading-tight">
                {firstYear.title}
              </h2>
              <p className="mt-3 font-serif italic text-lg text-gold">
                {firstYear.subtitle}
              </p>
              <div className="mt-7 space-y-5 text-stone2 leading-relaxed">
                {firstYear.description.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
              <p
                className="mt-5 text-stone2 leading-relaxed"
                data-testid="books-first-year-clarification"
              >
                The First Year After Goodbye is a separate bereavement book
                focusing on the experiences of grief during the first year and
                beyond. It also has its own 13-month guided companion
                workbook, distinct from the Bereavement Workbook associated
                with From Diagnosis to Goodbye&trade;.
              </p>
              <a
                href={firstYear.amazon}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="books-buy-kindle-first-year-after-goodbye"
                className="mt-8 inline-flex items-center gap-2 bg-gold text-navy px-6 py-3 rounded-full text-sm font-semibold hover:bg-gold-hover hover:text-ivory transition-colors duration-200"
              >
                Buy Kindle edition on Amazon <ExternalLink className="w-4 h-4" />
              </a>
            </Reveal>
          </div>
          <div className="lg:col-span-5 order-1 lg:order-2">
            <Reveal delay={0.1}>
              <div className="relative max-w-[300px] mx-auto lg:ml-auto">
                <div
                  className="absolute -inset-8 -z-10"
                  style={{
                    background:
                      "radial-gradient(circle at 50% 45%, rgba(126,42,56,0.16), transparent 68%)",
                  }}
                  aria-hidden="true"
                />
                <img
                  src={firstYear.cover}
                  alt={`Cover of ${firstYear.title}: ${firstYear.subtitle}`}
                  data-testid="books-feature-first-year"
                  className="w-full h-auto rounded-sm shadow-[0_36px_70px_-24px_rgba(11,21,34,0.5)] hover:-translate-y-2 transition-transform duration-500"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section
        className="mx-auto max-w-6xl px-5 sm:px-8 pb-24"
        data-testid="books-coming-soon-section"
      >
        <Reveal>
          <div className="bg-sand border border-line rounded-md overflow-hidden">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 p-8 sm:p-12 lg:p-16 items-center">
              <div className="lg:col-span-7">
                <img
                  src="/images/reflection-cards.jpg"
                  alt="The Compassionate Reflections Card Collection — five reflection cards inspired by From Diagnosis to Goodbye arranged on a table with a candle, a book, a pen, and a small golden heart"
                  data-testid="books-coming-soon-image"
                  className="w-full h-auto rounded-sm shadow-[0_28px_56px_-24px_rgba(11,21,34,0.45)]"
                  loading="lazy"
                />
              </div>
              <div className="lg:col-span-5">
                <span className="inline-block text-[10px] uppercase tracking-[0.18em] bg-gold-light text-gold-hover px-3 py-1 rounded-full">
                  Coming soon
                </span>
                <h3 className="mt-4 font-serif text-2xl sm:text-3xl text-navy leading-tight">
                  Compassionate Reflections Card Collection
                </h3>
                <p className="mt-3 font-serif italic text-lg text-gold">
                  Small moments of presence. Meaningful words for the journey.
                </p>
                <p className="mt-5 text-stone2 leading-relaxed">
                  A collection of compassionate reflection cards inspired by{" "}
                  <em>From Diagnosis to Goodbye</em>, created to encourage
                  presence, meaningful conversation, comfort, and reflection
                  throughout serious illness, caregiving, end of life, and
                  grief.
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-6xl px-5 sm:px-8 pb-24">
        <Reveal>
          <div className="bg-sand border border-line rounded-md p-8 sm:p-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div>
              <h2 className="font-serif text-2xl sm:text-3xl text-navy tracking-tight">
                Buying for a whole community?
              </h2>
              <p className="mt-3 text-stone2 leading-relaxed max-w-xl">
                Hospices, hospitals, senior living communities, churches,
                caregiver groups, and other organizations can request a quote
                for multiple copies of any title on this page.
              </p>
            </div>
            <Link
              to="/group-copies"
              data-testid="books-group-copies-cta"
              className="shrink-0 inline-flex items-center gap-2 bg-navy text-ivory px-7 py-3.5 rounded-full text-sm font-medium hover:bg-navy-light transition-colors duration-200"
            >
              Request group copies <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
      </section>

      <section className="relative overflow-hidden">
        <img
          src={IMAGES.path}
          alt="A winding path through a field in the last light of sunset"
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-navy-deep/80" />
        <div className="relative mx-auto max-w-6xl px-5 sm:px-8 py-24 text-center">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl text-ivory tracking-tight">
              Could a visit serve your community too?
            </h2>
            <p className="mt-4 text-ivory/70 max-w-xl mx-auto">
              Workshops, author conversations, and signings bring the
              book&rsquo;s work to the people who need it.
            </p>
            <Link
              to="/booking"
              data-testid="books-cta-booking"
              className="mt-9 inline-flex items-center gap-2 bg-gold text-navy px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-gold-hover hover:text-ivory transition-colors duration-200"
            >
              Invite Chaplain Derrick <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
